import {
  type AuctionListing,
  type AuctionSearchQuery,
  type AuctionSort,
  AUCTION_SORTS,
  type ItemSlot,
  ITEM_KINDS,
  type ItemKind,
} from '@shefira/shared';

import { buyListing, searchListings } from '../net/auction.js';
import {
  AUCTION_SLOT_LABELS,
  type AuctionDesk,
  failureText,
  KIND_LABELS,
  lotRow,
  priceTag,
  SORT_LABELS,
  timeLeft,
} from './auction-format.js';
import { formatGold } from './bench.js';
import { hideItemHover } from './item-hover.js';
import { uiFormat, uiText } from './text.js';

/**
 * « Acheter » (ADR 0035): a search box, three filters and the page of lots
 * they find, each a small card with its price set against the purse. A
 * purchase asks for a second click (« Sûr ? »), then goes over HTTP; the bag
 * and the purse follow through the room.
 */

export interface AuctionBuyOptions {
  /** A lot of mine cannot be bought: the button says so instead. */
  isMine: (listingId: string) => boolean;
  onBought: (listing: AuctionListing) => void;
}

/** Typing pauses this long before the search leaves. */
const SEARCH_DEBOUNCE_MS = 300;
/** How long « Sûr ? » waits for the click that buys. */
const BUY_CONFIRM_MS = 3000;

function isItemSlot(value: string): value is ItemSlot {
  return value in AUCTION_SLOT_LABELS;
}

function isKind(value: string): value is ItemKind {
  return (ITEM_KINDS as readonly string[]).includes(value);
}

function isSort(value: string): value is AuctionSort {
  return (AUCTION_SORTS as readonly string[]).includes(value);
}

export class AuctionBuyView {
  readonly root: HTMLElement;
  private readonly search: HTMLInputElement;
  private readonly slot: HTMLSelectElement;
  private readonly kind: HTMLSelectElement;
  private readonly sort: HTMLSelectElement;
  private readonly list: HTMLElement;
  private readonly status: HTMLElement;
  private readonly pager: HTMLElement;
  private readonly previous: HTMLButtonElement;
  private readonly next: HTMLButtonElement;
  private readonly pageLabel: HTMLElement;
  private page = 0;
  private hasMore = false;
  private listings: AuctionListing[] = [];
  /** Requests are numbered so a slow answer never overwrites a newer one. */
  private sequence = 0;
  private debounce: number | undefined;
  /** The lot whose « Acheter » was clicked once, and the timer that disarms it. */
  private armed: { id: string; timer: number } | undefined;
  private busy = false;

  constructor(
    private readonly desk: AuctionDesk,
    private readonly options: AuctionBuyOptions,
  ) {
    this.root = document.createElement('div');
    this.root.className = 'auction__view auction__view--buy';

    const filters = document.createElement('div');
    filters.className = 'auction__filters';
    this.search = document.createElement('input');
    this.search.type = 'search';
    this.search.className = 'auction__input auction__search';
    this.search.maxLength = 40;
    this.search.placeholder = uiText('Rechercher un objet');
    this.search.setAttribute('aria-label', uiText('Rechercher un objet'));
    this.search.addEventListener('input', () => {
      this.scheduleSearch();
    });
    this.slot = this.select('Emplacement', 'Emplacement', AUCTION_SLOT_LABELS);
    this.kind = this.select('Genre', 'Genre', KIND_LABELS);
    this.sort = this.select('Tri', null, SORT_LABELS);
    const selects = document.createElement('div');
    selects.className = 'auction__selects';
    // The chevron sits on a wrapper: the select's own frame is painted over its background.
    for (const select of [this.slot, this.kind, this.sort]) {
      const wrap = document.createElement('span');
      wrap.className = 'auction__select-wrap';
      wrap.append(select);
      selects.append(wrap);
    }
    filters.append(this.search, selects);
    // Typing here must not fire the game's shortcuts; Escape leaves the field.
    filters.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && event.target instanceof HTMLElement) event.target.blur();
      event.stopPropagation();
    });

    this.list = document.createElement('div');
    this.list.className = 'bench__list auction__list';
    this.status = document.createElement('p');
    this.status.className = 'auction__status';
    this.status.setAttribute('aria-live', 'polite');

    this.pager = document.createElement('div');
    this.pager.className = 'auction__pager';
    this.previous = this.pageButton('‹', 'Précédente', -1);
    this.pageLabel = document.createElement('span');
    this.pageLabel.className = 'auction__page';
    this.next = this.pageButton('›', 'Suivante', 1);
    this.pager.append(this.previous, this.pageLabel, this.next);
    this.pager.hidden = true;

    this.root.append(filters, this.status, this.list, this.pager);
  }

  /** Ask again with the current filters, on the current page. */
  refresh(): void {
    this.load(this.page);
  }

  destroy(): void {
    window.clearTimeout(this.debounce);
    this.disarm();
  }

  /** Draws the page already fetched again: the purse or my lots changed. */
  render(): void {
    if (this.root.hidden) return;
    hideItemHover();
    const scroll = this.list.scrollTop;
    this.list.replaceChildren();
    if (this.listings.length === 0 && this.status.textContent === '') {
      const empty = document.createElement('p');
      empty.className = 'bench__hint auction__empty';
      empty.textContent = uiText('Aucun lot ne correspond.');
      this.list.append(empty);
    }
    for (const listing of this.listings) this.list.append(this.row(listing));
    this.list.scrollTop = scroll;
    this.previous.disabled = this.page === 0;
    this.next.disabled = !this.hasMore;
    this.pager.hidden = this.page === 0 && !this.hasMore;
    this.pageLabel.textContent = uiFormat('Page {page}', { page: this.page + 1 });
  }

  private pageButton(glyph: string, label: string, step: number): HTMLButtonElement {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'smith__go auction__page-button';
    button.textContent = glyph;
    button.title = uiText(label);
    button.setAttribute('aria-label', uiText(label));
    button.addEventListener('click', () => {
      this.load(this.page + step);
      this.list.scrollTop = 0;
    });
    return button;
  }

  private select(
    label: string,
    anyLabel: string | null,
    labels: Partial<Record<string, string>>,
  ): HTMLSelectElement {
    const select = document.createElement('select');
    select.className = 'auction__input auction__select';
    select.setAttribute('aria-label', uiText(label));
    if (anyLabel !== null) select.append(new Option(uiText(anyLabel), ''));
    for (const [value, text] of Object.entries(labels)) {
      if (text !== undefined) select.append(new Option(uiText(text), value));
    }
    select.addEventListener('change', () => {
      this.load(0);
    });
    return select;
  }

  private scheduleSearch(): void {
    window.clearTimeout(this.debounce);
    this.debounce = window.setTimeout(() => {
      this.load(0);
    }, SEARCH_DEBOUNCE_MS);
  }

  private query(page: number): Partial<AuctionSearchQuery> {
    const query: Partial<AuctionSearchQuery> = { page };
    const q = this.search.value.trim();
    if (q !== '') query.q = q;
    if (isItemSlot(this.slot.value)) query.slot = this.slot.value;
    if (isKind(this.kind.value)) query.kind = this.kind.value;
    if (isSort(this.sort.value)) query.sort = this.sort.value;
    return query;
  }

  private load(page: number): void {
    const sequence = ++this.sequence;
    this.disarm();
    this.status.textContent = uiText('Recherche…');
    this.status.dataset.tone = '';
    void this.desk
      .access()
      .then((ticket) => searchListings(this.desk.token, ticket, this.query(Math.max(0, page))))
      .then((response) => {
        if (sequence !== this.sequence) return;
        this.page = response.page;
        this.hasMore = response.hasMore;
        this.listings = response.listings;
        this.status.textContent = '';
        this.render();
      })
      .catch((error: unknown) => {
        if (sequence !== this.sequence) return;
        this.status.textContent = uiText(failureText(error));
        this.status.dataset.tone = 'refused';
      });
  }

  private disarm(): void {
    if (this.armed === undefined) return;
    window.clearTimeout(this.armed.timer);
    this.armed = undefined;
  }

  private row(listing: AuctionListing): HTMLElement {
    const { row, text, meta, actions } = lotRow(
      listing,
      this.desk.icons,
      this.desk.wearer,
      () => this.desk.inventory()?.equipped,
    );
    meta.textContent =
      listing.quantity > 1
        ? uiFormat('{seller} · {time} · {each} l’unité', {
            seller: listing.sellerName,
            time: timeLeft(listing.expiresAt),
            each: formatGold(Math.round(listing.price / listing.quantity)),
          })
        : uiFormat('{seller} · {time}', {
            seller: listing.sellerName,
            time: timeLeft(listing.expiresAt),
          });

    const mine = this.options.isMine(listing.id);
    const purse = this.desk.inventory()?.gold ?? 0;
    const short = !mine && purse < listing.price;
    if (short) {
      const missing = document.createElement('span');
      missing.className = 'bench__meta bench__meta--short';
      missing.textContent = uiFormat('Il te manque {gold}', {
        gold: formatGold(listing.price - purse),
      });
      text.append(missing);
    }

    const armed = this.armed?.id === listing.id;
    const buy = document.createElement('button');
    buy.type = 'button';
    buy.className = 'smith__go auction__buy';
    buy.textContent = uiText(mine ? 'Ton lot' : armed ? 'Sûr ?' : 'Acheter');
    if (armed) buy.classList.add('auction__buy--armed');
    buy.disabled = mine || short || this.busy;
    if (short) buy.title = uiText('Ta bourse n’y suffit pas.');
    buy.addEventListener('click', () => {
      this.onBuyClick(listing);
    });
    actions.append(priceTag(listing.price, short), buy);
    return row;
  }

  /** First click arms the lot for a few seconds; the second one buys. */
  private onBuyClick(listing: AuctionListing): void {
    if (this.armed?.id !== listing.id) {
      this.disarm();
      const timer = window.setTimeout(() => {
        this.armed = undefined;
        this.render();
      }, BUY_CONFIRM_MS);
      this.armed = { id: listing.id, timer };
      this.render();
      return;
    }
    this.disarm();
    this.busy = true;
    this.render();
    void this.desk
      .access()
      .then((ticket) => buyListing(this.desk.token, ticket, listing.id))
      .then((bought) => {
        this.desk.say(uiText('Acheté ! Le lot est dans ton sac.'), 'success');
        this.options.onBought(bought);
      })
      .catch((error: unknown) => {
        this.desk.say(uiText(failureText(error)), 'refused');
      })
      .finally(() => {
        this.busy = false;
        this.refresh();
      });
  }
}
